import { z } from 'zod';
import type { UseFormReturn } from 'react-hook-form';
import { FormDialog } from '@/components/organisms/FormDialog';
import { Form } from '@/components/organisms/Form';
import { TextField } from '@/components/organisms/FormFields';
import { Button } from '@/components/ui/button';
import { getErrorMessage } from '@/lib/errors';
import { useAuthStore } from '@/lib/stores/auth';
import { useProfileQuery, useUpdateProfileMutation } from '@/features/settings/queries';

const nameSchema = z.object({
  name: z.string().trim().min(1, 'Enter your name').max(255, 'Keep it under 255 characters'),
});

/**
 * Asks a signed-in user with no name for one, and won't close until they give
 * it. Saleshandy-provisioned owners can arrive without a name: they sign
 * straight into their org and never pass the setup screen that would have
 * asked. Saving also marks the profile complete server-side
 * (`ProfileService.updateProfile`). Keyed on the name itself rather than on
 * `profileCompleted`, so a migrated user who has a name never sees it.
 */
export function RequiredNameDialog() {
  const profile = useProfileQuery();
  const updateProfile = useUpdateProfileMutation();
  const open = profile.isSuccess && !profile.data.name?.trim();

  async function save(
    { name }: z.infer<typeof nameSchema>,
    form: UseFormReturn<z.infer<typeof nameSchema>>,
  ) {
    try {
      const updated = await updateProfile.mutateAsync({ name });
      const current = useAuthStore.getState();
      if (current.user) useAuthStore.setState({ user: { ...current.user, name: updated.name } });
    } catch (err) {
      // The mutation is silent (no toast), so the failure shows on the field.
      form.setError('name', { message: getErrorMessage(err, "Couldn't save your name. Try again.") });
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={() => {}}
      dismissible={false}
      title="What's your name?"
      description="Your teammates see it next to your activity."
    >
      <Form schema={nameSchema} defaultValues={{ name: '' }} onSubmit={save}>
        {({ form }) => (
          <div className="flex flex-col gap-4">
            <TextField name="name" label="Full name" autoComplete="name" placeholder="Jane Doe" />
            <div className="flex justify-end">
              <Button type="submit" disabled={form.formState.isSubmitting}>
                Save
              </Button>
            </div>
          </div>
        )}
      </Form>
    </FormDialog>
  );
}
